/**
 * The government screen (F3): what you live under, what you could choose, and what
 * changing costs (ADR-0059).
 *
 * Every line describing a government is written from its numbers in governments.json,
 * so the screen cannot promise something the engine does not do.
 */

import type { Content, GovernmentDef } from '../../content/schema/index.js';
import type { Engine } from '../core/engine.js';
import { ERROR_TEXT } from '../core/engine.js';
import { anarchyLeft, changeBlocker, governmentOf, inAnarchy, wearyCitizens } from '../core/government/index.js';

export interface GovernmentScreen {
  open(): void;
  close(): void;
  readonly isOpen: boolean;
  refresh(): void;
}

export interface GovernmentScreenOptions {
  getEngine: () => Engine;
  /** Ask before a revolution; resolves true to go ahead. */
  confirm: (title: string, body: string) => Promise<boolean>;
  onChanged: () => void;
  log: (message: string, kind?: 'ok' | 'err' | 'note') => void;
}

/** Plain-English lines for a government's effects, from its numbers. */
export function describeGovernment(content: Content, def: GovernmentDef): Array<{ text: string; good: boolean | null }> {
  const e = def.effects;
  const lines: Array<{ text: string; good: boolean | null }> = [];
  const cap = Math.min(content.rules.research.maxRate, e.maxScienceRate);
  lines.push({ text: `Science up to ${cap}% of commerce`, good: cap >= 80 ? true : cap <= 60 ? false : null });
  if (e.tilePenalty) lines.push({ text: 'Rich tiles waste a point: any yield above 2 gives one less', good: false });
  if (e.goldTileBonus > 0) lines.push({ text: `+${e.goldTileBonus} gold on tiles already yielding ${e.goldTileMinimum} or more`, good: true });
  if (e.dragFlat > 0) lines.push({ text: `Every city but the capital loses ${Math.round(e.dragFlat * 100)}%, however far away`, good: null });
  if (e.dragMultiplier !== 1 && e.dragMultiplier > 0) {
    lines.push({
      text: e.dragMultiplier > 1 ? `Distance from the capital costs ${Math.round(e.dragMultiplier * 100)}% as much` : `Distance from the capital costs only ${Math.round(e.dragMultiplier * 100)}% as much`,
      good: e.dragMultiplier < 1,
    });
  }
  lines.push({ text: `${e.freeUnitsPerCity} unit${e.freeUnitsPerCity === 1 ? '' : 's'} per city supported free`, good: e.freeUnitsPerCity >= 3 ? true : e.freeUnitsPerCity <= 1 ? false : null });
  if (e.contentment > 0) lines.push({ text: `${e.contentment} more content citizen${e.contentment === 1 ? '' : 's'} in every city`, good: true });
  lines.push(
    e.maxGarrisonContentment > 0
      ? { text: `Soldiers keep order: ${e.garrisonContentment} content each, up to ${e.maxGarrisonContentment}`, good: e.maxGarrisonContentment >= 3 ? true : null }
      : { text: 'Soldiers in a city keep nobody content', good: false },
  );
  lines.push(
    e.warWeariness === 0
      ? { text: 'War losses do not trouble the people', good: true }
      : { text: `War losses breed discontent (×${e.warWeariness})`, good: e.warWeariness <= 0.5 ? null : false },
  );
  if (e.productionPercent !== 0) lines.push({ text: `${e.productionPercent > 0 ? '+' : ''}${e.productionPercent}% production in every city`, good: e.productionPercent > 0 });
  if (e.culturePercent !== 0) lines.push({ text: `${e.culturePercent > 0 ? '+' : ''}${e.culturePercent}% culture in every city`, good: e.culturePercent > 0 });
  return lines;
}

export function createGovernmentScreen(options: GovernmentScreenOptions): GovernmentScreen {
  const root = document.createElement('div');
  root.className = 'cities-screen';
  root.hidden = true;
  root.innerHTML = `
    <div class="cities-panel gov-panel">
      <header>
        <h2>Government</h2>
        <div class="cs-sub" id="gv-sub"></div>
        <button id="gv-close" title="Close (Esc)">Close</button>
      </header>
      <div class="cts-body"><div id="gv-list" class="gv-list"></div></div>
    </div>
  `;
  document.body.append(root);
  const $ = <T extends HTMLElement>(id: string): T => root.querySelector(`#${id}`) as T;

  function refresh(): void {
    const engine = options.getEngine();
    const { state, content } = engine;
    const civ = state.civs[engine.activeCiv];
    if (!civ) return;
    const now = governmentOf(content, civ);
    const weary = wearyCitizens(state, content, civ.id);
    const anarchy = inAnarchy(civ);
    const turns = content.rules.government.anarchyTurns;
    $('gv-sub').textContent =
      (anarchy
        ? `Anarchy — ${content.governments.get(civ.nextGovernment).name} in ${anarchyLeft(state, civ)} turn${anarchyLeft(state, civ) === 1 ? '' : 's'}`
        : `You live under ${now.name}, since turn ${civ.governmentSince}`) +
      ` · war weariness: ${weary > 0 ? `${weary} discontented in every city` : 'none'}`;

    const cards = content.governments.all
      .filter((g) => g.selectable)
      .map((g) => {
        const current = g.id === civ.government || g.id === civ.nextGovernment;
        const blocker = changeBlocker(content, civ, g.id);
        let action: string;
        if (g.id === civ.government) action = '<span class="ok">Your government</span>';
        else if (g.id === civ.nextGovernment) action = '<span class="soon">Coming after the anarchy</span>';
        else if (blocker === 'NeedsTech') action = `<span class="dim">Needs ${escapeHtml(content.technologies.get(g.techId).name)}</span>`;
        else if (blocker === 'InRevolution') action = '<span class="dim">Wait for the revolution to end</span>';
        else action = `<button data-adopt="${g.id}" class="primary" title="${turns} turn${turns === 1 ? '' : 's'} of anarchy first">Adopt</button>`;
        const lines = describeGovernment(content, g)
          .map((l) => `<li class="${l.good === true ? 'good' : l.good === false ? 'bad' : ''}">${escapeHtml(l.text)}</li>`)
          .join('');
        return `<div class="gv-card${current ? ' current' : ''}${blocker === 'NeedsTech' ? ' locked' : ''}">
          <div class="gv-head"><b>${escapeHtml(g.name)}</b><span class="gv-action">${action}</span></div>
          <div class="dim gv-summary">${escapeHtml(g.summary)}</div>
          <ul class="gv-effects">${lines}</ul>
        </div>`;
      })
      .join('');
    $('gv-list').innerHTML = cards;

    for (const button of root.querySelectorAll<HTMLButtonElement>('button[data-adopt]')) {
      button.addEventListener('click', () => {
        const target = Number(button.dataset['adopt']);
        const name = content.governments.get(target).name;
        void options
          .confirm(
            `Revolution: ${name}?`,
            `Your government falls at once: ${turns} turn${turns === 1 ? '' : 's'} of anarchy, starting now — no science, much of your gold lost to disorder, and rich land wasted.\n` +
              `Then ${name} takes over.`,
          )
          .then((yes) => {
            if (!yes) return;
            const result = engine.apply({ t: 'ChangeGovernment', government: target });
            if (result.ok) options.log(`Revolution! ${name} in ${turns} turn${turns === 1 ? '' : 's'}.`, 'note');
            else options.log(ERROR_TEXT[result.error], 'err');
            options.onChanged();
            refresh();
          });
      });
    }
  }

  function open(): void {
    root.hidden = false;
    refresh();
  }
  function close(): void {
    root.hidden = true;
  }
  $('gv-close').addEventListener('click', close);
  root.addEventListener('click', (event) => {
    if (event.target === root) close();
  });

  return {
    open,
    close,
    get isOpen(): boolean {
      return !root.hidden;
    },
    refresh,
  };
}

const escapeHtml = (text: string): string => text.replace(/[&<>"']/g, (c) => `&#${c.charCodeAt(0)};`);
